import type { ProjectMember } from '@outpost/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';

import { FilterTrigger } from '@/components/filter-dropdown';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/lib/auth';
import { initials } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * Everyone who can see the project, which is exactly who may hold one of its
 * issues: the server refuses an assignee who cannot open what they are given.
 */
export function useProjectMembers(projectId: string) {
  const { client } = useAuth();
  return useQuery({
    queryKey: ['project-members', projectId],
    queryFn: () => client.listProjectMembers(projectId),
    // Membership changes far more slowly than the issue stream it decorates.
    staleTime: 5 * 60_000,
  });
}

export function memberName(member: Pick<ProjectMember, 'name' | 'email'>): string {
  return member.name.trim() || member.email;
}

/** Initials in a circle, standing in for the avatars Outpost does not store. */
export function AssigneeAvatar({
  member,
  className,
}: {
  member: Pick<ProjectMember, 'name' | 'email'>;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-[9px] font-semibold uppercase text-muted-foreground',
        className,
      )}
    >
      {initials(member.name, member.email)}
    </span>
  );
}

/**
 * Assign an issue, or hand it back to nobody.
 *
 * `compact` is the issue-stream variant: an avatar alone, so a row of them can
 * be scanned down the list and triage does not need the detail page.
 */
export function AssigneePicker({
  projectId,
  issueId,
  assigneeId,
  compact = false,
}: {
  projectId: string;
  issueId: string;
  assigneeId: string | null;
  compact?: boolean;
}) {
  const { client, user } = useAuth();
  const queryClient = useQueryClient();
  const members = useProjectMembers(projectId);

  const assign = useMutation({
    mutationFn: (userId: string | null) => client.updateIssue(issueId, { assignee_id: userId }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['issue', issueId], updated);
      void queryClient.invalidateQueries({ queryKey: ['issues', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['issue-activity', issueId] });
    },
  });

  const assignee = members.data?.find((member) => member.id === assigneeId) ?? null;
  // Someone can lose access to a project while still holding its issues, and
  // the row must stay assignable rather than render an empty circle.
  const orphaned = assigneeId !== null && members.isSuccess && !assignee;

  // Yourself first: taking an issue is the assignment people make most.
  const options = [...(members.data ?? [])].sort(
    (a, b) => Number(b.id === user?.id) - Number(a.id === user?.id),
  );

  const label = assignee ? memberName(assignee) : orphaned ? 'Former member' : 'Unassigned';
  const title = assign.error
    ? assign.error.message
    : orphaned
      ? 'Assigned to someone who no longer has access to this project'
      : assignee
        ? `Assigned to ${memberName(assignee)}`
        : 'Nobody is assigned';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {compact ? (
          <button
            type="button"
            title={title}
            aria-label={`Assignee: ${label}`}
            disabled={assign.isPending}
            className={cn(
              'inline-flex size-6 items-center justify-center rounded-full transition-colors hover:bg-accent',
              assign.isPending && 'opacity-60',
            )}
          >
            {assignee ? (
              <AssigneeAvatar member={assignee} />
            ) : (
              // Dimmed: most issues are unassigned, and a full-strength icon
              // on every row would read as a column of prompts.
              <UserPlus
                className={cn(
                  'size-3.5',
                  assign.error
                    ? 'text-destructive'
                    : 'text-muted-foreground/60 hover:text-muted-foreground',
                )}
              />
            )}
          </button>
        ) : (
          <Button
            variant="outline"
            title={title}
            disabled={assign.isPending}
            className={cn(assign.error && 'border-destructive text-destructive')}
          >
            {assignee ? <AssigneeAvatar member={assignee} /> : <UserPlus />}
            {label}
          </Button>
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="max-h-80 w-64 overflow-y-auto">
        <DropdownMenuLabel>Assign to</DropdownMenuLabel>
        {options.map((member) => (
          <DropdownMenuCheckboxItem
            key={member.id}
            checked={member.id === assigneeId}
            onCheckedChange={() => assign.mutate(member.id === assigneeId ? null : member.id)}
          >
            <span className="flex min-w-0 items-center gap-2">
              <AssigneeAvatar member={member} />
              <span className="min-w-0">
                <span className="block truncate text-sm">
                  {memberName(member)}
                  {member.id === user?.id && <span className="text-muted-foreground"> (you)</span>}
                </span>
                {/* Only when it adds something: an account without a name is
                    already shown by its address on the line above. */}
                {memberName(member) !== member.email && (
                  <span className="block truncate text-xs text-muted-foreground">
                    {member.email}
                  </span>
                )}
              </span>
            </span>
          </DropdownMenuCheckboxItem>
        ))}
        {members.isError && (
          <p className="px-2 py-1.5 text-xs text-muted-foreground">
            The project's members could not be loaded.
          </p>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          checked={assigneeId === null}
          onCheckedChange={() => {
            if (assigneeId !== null) assign.mutate(null);
          }}
        >
          <span className="text-sm">Unassigned</span>
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Filter the issue stream by who holds an issue. Single-select, because the
 * search grammar keeps only the last `assigned:` term it reads.
 */
export function AssigneeFilter({
  projectId,
  selected,
  onChange,
}: {
  projectId: string;
  selected: string | null;
  onChange: (value: string | null) => void;
}) {
  const { user } = useAuth();
  const members = useProjectMembers(projectId);

  // `me` and `none` are the server's own spellings, so a query typed by hand
  // and one built here are the same query.
  const others = (members.data ?? []).filter((member) => member.id !== user?.id);
  const named = others.find((member) => member.email.toLowerCase() === selected?.toLowerCase());
  const summary =
    selected === null
      ? null
      : selected === 'me'
        ? 'Me'
        : selected === 'none' || selected === 'unassigned'
          ? 'Unassigned'
          : (named && memberName(named)) || selected;

  const choose = (value: string) => onChange(value === selected ? null : value);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <FilterTrigger label="Assignee" summary={summary} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-80 w-64 overflow-y-auto">
        <DropdownMenuLabel>Assignee</DropdownMenuLabel>
        <DropdownMenuCheckboxItem checked={selected === 'me'} onCheckedChange={() => choose('me')}>
          <span className="text-sm">Me</span>
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={selected === 'none' || selected === 'unassigned'}
          onCheckedChange={() => choose('none')}
        >
          <span className="text-sm">Unassigned</span>
        </DropdownMenuCheckboxItem>
        {others.length > 0 && <DropdownMenuSeparator />}
        {others.map((member) => (
          <DropdownMenuCheckboxItem
            key={member.id}
            checked={member.id === named?.id}
            onCheckedChange={() => choose(member.email)}
          >
            <span className="flex min-w-0 items-center gap-2">
              <AssigneeAvatar member={member} />
              <span className="min-w-0 truncate text-sm">{memberName(member)}</span>
            </span>
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
